// @ts-nocheck
import { useState, useEffect } from "react";
import {
  Card, Form, TimePicker, Switch, Button,
  Typography, Divider, DatePicker, Input, Table, Popconfirm, message, Space,
} from "../components/admin-ui";
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
import { formatDateOnly } from "../utils/formatLocaleDate";
import {
  useKopitiamHours, useUpdateHours,
  useHolidays, useAddHoliday, useUpdateHoliday, useDeleteHoliday,
} from "../api/hooks";

const { Text } = Typography;
const { color: T } = tokens;

interface HourRow { dayOfWeek: number; openTime: string; closeTime: string; isClosed: boolean }
interface HolidayRow { id: string; date: string; note: string | null }

// Keep the dedicated Hours screen aligned with the defaults already used by
// Settings.tsx. A new kopitiam can legitimately have no KopitiamHours rows
// until its first save, but the editor must still render all seven days.
const DEFAULT_HOUR_ROWS: HourRow[] = [
  { dayOfWeek: 0, openTime: "08:00", closeTime: "22:00", isClosed: true },
  { dayOfWeek: 1, openTime: "08:00", closeTime: "22:00", isClosed: false },
  { dayOfWeek: 2, openTime: "08:00", closeTime: "22:00", isClosed: false },
  { dayOfWeek: 3, openTime: "08:00", closeTime: "22:00", isClosed: false },
  { dayOfWeek: 4, openTime: "08:00", closeTime: "22:00", isClosed: false },
  { dayOfWeek: 5, openTime: "08:00", closeTime: "23:30", isClosed: false },
  { dayOfWeek: 6, openTime: "09:00", closeTime: "23:30", isClosed: false },
];

export function HoursScreen() {
  const { t, i18n } = useTranslation();
  const { data: hoursData, isLoading: hoursLoading } = useKopitiamHours();
  const { data: holidaysData, isLoading: holidaysLoading } = useHolidays();
  const updateHours = useUpdateHours();
  const addHoliday = useAddHoliday();
  const updateHoliday = useUpdateHoliday();
  const deleteHoliday = useDeleteHoliday();

  const [rows, setRows] = useState<HourRow[]>(DEFAULT_HOUR_ROWS);
  const [addForm] = Form.useForm<{ date: dayjs.Dayjs; note?: string }>();
  const [editingHoliday, setEditingHoliday] = useState<{ id: string; date: dayjs.Dayjs; note: string } | null>(null);

  const DAY_NAMES = [
    t("hours.days.sunday"),
    t("hours.days.monday"),
    t("hours.days.tuesday"),
    t("hours.days.wednesday"),
    t("hours.days.thursday"),
    t("hours.days.friday"),
    t("hours.days.saturday"),
  ];

  useEffect(() => {
    if (!hoursData?.data) return;
    const savedByDay = new Map(hoursData.data.map((row) => [row.dayOfWeek, row]));
    setRows(DEFAULT_HOUR_ROWS.map((fallback) => ({ ...fallback, ...savedByDay.get(fallback.dayOfWeek) })));
  }, [hoursData]);

  const updateRow = (dayOfWeek: number, changes: Partial<HourRow>) => {
    setRows((prev) => prev.map((r) => r.dayOfWeek === dayOfWeek ? { ...r, ...changes } : r));
  };

  const saveHours = async () => {
    try {
      await updateHours.mutateAsync(rows);
      void message.success(t("hours.saveSuccess"));
    } catch {
      void message.error(t("hours.saveFailed"));
    }
  };

  const onAddHoliday = async (values: { date: dayjs.Dayjs; note?: string }) => {
    try {
      await addHoliday.mutateAsync({ date: values.date.format("YYYY-MM-DD"), note: values.note });
      void message.success(t("hours.holidayAdded"));
      addForm.resetFields();
    } catch {
      void message.error(t("hours.holidayAddFailed"));
    }
  };

  return (
    <AdminShell>
      <div className="admin-hours-page admin-list-page">
        <div className="admin-hours-inner">
      <div className="admin-hours-toolbar">
        <div>
          <h1 className="admin-page-title">{t("hours.title")}</h1>
          <p className="admin-hours-subtitle">{t("hours.subtitle")}</p>
        </div>
      </div>

      <div className="admin-hours-layout">
        {/* Operating Hours */}
          <Card
            className="admin-hours-card admin-hours-operating admin-settings-card"
            title={t("hours.cardOperatingHours")}
            loading={hoursLoading}
            extra={
              <Button className="admin-hours-save" type="primary" size="small" onClick={saveHours} loading={updateHours.isPending}>
                {t("hours.saveHours")}
              </Button>
            }
          >
            <div className="admin-hours-week">
            {rows.map((row) => (
              <div className="admin-hours-day-row" data-closed={row.isClosed} key={row.dayOfWeek}>
                <Text className="admin-hours-day-name">{DAY_NAMES[row.dayOfWeek]}</Text>
                <span className="admin-hours-status-copy">{row.isClosed ? t("hours.closed") : t("hours.open")}</span>
                <Switch
                  checked={!row.isClosed}
                  onChange={(v) => updateRow(row.dayOfWeek, { isClosed: !v })}
                  checkedChildren={t("hours.open")}
                  unCheckedChildren={t("hours.closed")}
                  size="small"
                />
                {!row.isClosed && (
                  <div className="admin-hours-time-range">
                    <TimePicker
                      value={dayjs(row.openTime, "HH:mm")}
                      format="HH:mm"
                      minuteStep={30}
                      size="small"
                      onChange={(t_) => t_ && updateRow(row.dayOfWeek, { openTime: t_.format("HH:mm") })}
                    />
                    <Text type="secondary" className="admin-hours-time-separator">–</Text>
                    <TimePicker
                      value={dayjs(row.closeTime, "HH:mm")}
                      format="HH:mm"
                      minuteStep={30}
                      size="small"
                      onChange={(t_) => t_ && updateRow(row.dayOfWeek, { closeTime: t_.format("HH:mm") })}
                    />
                  </div>
                )}
              </div>
            ))}
            </div>
          </Card>

        {/* Holidays */}
          <Card className="admin-hours-card admin-hours-holidays admin-settings-card" title={t("hours.cardHolidays")} loading={holidaysLoading}>
            <Form className="admin-hours-holiday-form admin-txn-filter-panel" form={addForm} layout="inline" onFinish={onAddHoliday}>
              <Form.Item className="admin-hours-date-field" name="date" rules={[{ required: true, message: t("hours.dateRequired") }]}>
                <DatePicker placeholder={t("hours.datePickerPlaceholder")} size="small" />
              </Form.Item>
              <Form.Item className="admin-hours-note-field" name="note">
                <Input placeholder={t("hours.notePlaceholder")} size="small" />
              </Form.Item>
              <Form.Item className="admin-hours-add-field">
                <Button className="admin-hours-add" type="primary" htmlType="submit" size="small" icon={<PlusOutlined />} loading={addHoliday.isPending}>
                  {t("hours.addHolidayBtn")}
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: "8px 0 12px" }} />

            <div className="admin-hours-table-panel admin-data-table-panel">
            <Table<HolidayRow>
              size="small"
              dataSource={(holidaysData?.data ?? []) as HolidayRow[]}
              rowKey="id"
              columns={[
                {
                  title: t("hours.colDate"), dataIndex: "date", key: "date",
                  render: (v: string, row) => editingHoliday?.id === row.id
                    ? <DatePicker size="small" value={editingHoliday.date} onChange={(d) => d && setEditingHoliday({ ...editingHoliday, date: d })} />
                    : <Text strong className="admin-data-time">{formatDateOnly(v, i18n.language)}</Text>,
                },
                {
                  title: t("hours.colNote"), dataIndex: "note", key: "note",
                  render: (v: string | null, row) => editingHoliday?.id === row.id
                    ? <Input size="small" value={editingHoliday.note} onChange={(e) => setEditingHoliday({ ...editingHoliday, note: e.target.value })} />
                    : <span className="admin-hours-holiday-note">{v ?? <Text type="secondary">—</Text>}</span>,
                },
                {
                  title: "", key: "actions", width: 96,
                  render: (_, row) => editingHoliday?.id === row.id ? (
                    <Space size={2}>
                      <Button className="admin-hours-row-action"
                        size="small" type="text" icon={<CheckOutlined />}
                        loading={updateHoliday.isPending}
                        onClick={async () => {
                          try {
                            await updateHoliday.mutateAsync({
                              id: row.id,
                              date: editingHoliday.date.format("YYYY-MM-DD"),
                              note: editingHoliday.note,
                            });
                            void message.success(t("hours.updateHolidaySuccess"));
                            setEditingHoliday(null);
                          } catch {
                            void message.error(t("hours.updateHolidayFailed"));
                          }
                        }}
                      />
                      <Button className="admin-hours-row-action" size="small" type="text" icon={<CloseOutlined />} onClick={() => setEditingHoliday(null)} />
                    </Space>
                  ) : (
                    <Space size={2}>
                      <Button className="admin-hours-row-action"
                        size="small" type="text" icon={<EditOutlined />}
                        onClick={() => setEditingHoliday({ id: row.id, date: dayjs(row.date), note: row.note ?? "" })}
                      />
                      <Popconfirm title={t("hours.confirmRemoveHoliday")} onConfirm={() => void deleteHoliday.mutateAsync(row.id)}>
                        <Button className="admin-hours-row-action" size="small" danger icon={<DeleteOutlined />} type="text" />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
            </div>
          </Card>
      </div>
        </div>
      </div>
    </AdminShell>
  );
}
